import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';

import { FixtureType } from '@cds/betting-offer';
import { Sport, TagCount, TagType } from '@cds/betting-offer/tags';
import { OfferMapping } from '@cds/query-objects';
import { BettingOfferApi } from '@frontend/sports/betting-offer/feature/offer-service';
import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { StylesLazyLoadingService } from '@frontend/sports/deferred/feature/styles-loader';
import { WinGameFixture, toWinGameFixture } from '@frontend/sports/racing/feature/models';
import { NativeAppService } from '@frontend/vanilla/core';
import { orderBy } from 'lodash-es';
import { firstValueFrom, map, of, switchMap } from 'rxjs';

export interface AntepostFixtureGroup {
    fixtureGroup: TagCount;
    fixtures: WinGameFixture[];
}

export interface AntepostFixtureListModel {
    title: string;
    sport: Sport;
    fixtureGroups: AntepostFixtureGroup[];
}

@Injectable()
export class AntepostFixtureListResolver {
    constructor(
        private apiService: BettingOfferApi,
        private sitecore: Sitecore,
        private stylesService: StylesLazyLoadingService,
        private nativeAppService: NativeAppService,
    ) {}

    async resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<AntepostFixtureListModel> {
        const sport = route.data.sport;
        const title = sport === SportConstant.Golf ? this.sitecore.fixture.common.EachWayMarkets : '';
        const defaultSport: Sport = {
            id: sport,
            name: { value: '', sign: '' },
            type: TagType.Sport,
        };
        const empty: AntepostFixtureListModel = {
            sport: defaultSport,
            title: '',
            fixtureGroups: [],
        };

        const data = firstValueFrom(
            this.apiService.getCountsPerSport(sport, TagType.FixtureGroup).pipe(
                switchMap((counts) => {
                    if (!counts) {
                        return of(empty);
                    }

                    return this.apiService
                        .getFixtureList({
                            sportIds: sport,
                            fixtureTypes: FixtureType.Antepost,
                            offerMapping: OfferMapping.None,
                            fixtureGroupIds: counts.map((c) => c.tag.id).join(','),
                        })
                        .pipe(
                            map((result) => {
                                if (!result) {
                                    return empty;
                                }

                                let fixtureGroups = counts.map((fixtureGroup) => ({
                                    fixtureGroup,
                                    fixtures: result.fixtures.filter((f) => f.fixtureGroup.id === fixtureGroup.tag.id).map(toWinGameFixture),
                                }));

                                fixtureGroups = orderBy(fixtureGroups, (fg) => fg.fixtureGroup.rank);

                                return {
                                    title,
                                    sport: result.fixtures.map((f) => f.sport)[0] || defaultSport,
                                    fixtureGroups,
                                };
                            }),
                        );
                }),
            ),
        );

        const stylesPromise = this.nativeAppService.isTerminal ? Promise.resolve() : this.stylesService.lazyLoad('racing');
        const [model] = await Promise.all([data, stylesPromise]);

        return model;
    }
}
